import React, { useEffect, useState } from 'react';
import { IAuditLogSearchFormValues, initialAuditLogSearchValues } from 'models/auditLog';
import { useForm } from 'react-hook-form';
import Grid from '@mui/material/Grid';
import { SearchWrapper } from 'components/atoms/SearchWrapper';
import { FormDatePicker } from 'components/molecules/FormDatePicker';
import { useAuditLog } from 'pages/AuditLogPage/hooks';
import { useLang } from 'models/langContext';
import { EDictionaryTypes, IDictionary } from 'models/dictionary';
import { mapDictionaryToOptionProp } from 'helpers/dictionary';
import { useUsers } from 'pages/UsersPage/hooks';
import { IFormElementOptionProp } from 'models/form';
import Button from 'components/atoms/Button';
import { EButtonSizes, EButtonVariants } from 'constants/Buttons';
import { useGlobalProperty } from 'models/globalPropertyContext';
import { isDateAfter } from 'utils/date';
import { useTranslations } from 'hooks/useTranslations';
import { getEnumByValue } from 'utils/enum';
import { StyledSearchForm } from 'theme/styles';
import { IUser } from 'models/user';
import { SelectedFilters } from 'components/organisms/SelectedFilters';
import { useWeaponLicenceModule } from 'hooks/useWeaponLicenceModule';
import { prepareFilters } from 'helpers/filters';
import { EPerPages } from 'models/table';
import { AuditLogModules, EventTypes } from '../helpers';
import { FormMultiSelect } from 'components/molecules/FormMultiSelect';
import { filterEventName } from 'pages/AuditLogPage/AuditLogSearchForm/filterEventName';

interface IAuditLogSearchForm {
  onSubmit: (arg: IAuditLogSearchFormValues) => void;
  isSearchButtonDisabled?: boolean;
  initialSearchForm: IAuditLogSearchFormValues;
  currentPage: number;
  perPage: EPerPages;
}

enum EAuditLogSearchFormLabels {
  accountsUniqueIds = 'searchForAuditLog.auditLogTable.eventAuthor.column',
  modulesUniqueIds = 'searchForAuditLog.auditLogTable.module.column',
  eventTypesUniqueIds = 'searchForAuditLog.auditLogTable.eventType.column',
  eventNamesUniqueIds = 'searchForAuditLog.auditLogTable.eventName.column',
  eventDateFrom = 'searchForAuditLog.auditLogTable.dateFrom.column',
  eventDateTo = 'searchForAuditLog.auditLogTable.dateTo.column',
}

const filters: Record<AuditLogModules, EventTypes[]> = {
  [AuditLogModules.AUDIT_LOG]: [EventTypes.DATA_EXPORT, EventTypes.VIEW, EventTypes.SEARCH],
  [AuditLogModules.AUTHENTICATION]: [
    EventTypes.LOGIN,
    EventTypes.LOGOUT,
    EventTypes.PASSWORD_CHANGE,
    EventTypes.BLOCK,
  ],
  [AuditLogModules.LEGAL_ENTITIES]: [
    EventTypes.DATA_EXPORT,
    EventTypes.ADD,
    EventTypes.EDIT,
    EventTypes.DELETE,
    EventTypes.VIEW,
    EventTypes.SEARCH,
    EventTypes.DISABLE,
    EventTypes.REENABLE,
  ],
  [AuditLogModules.FIREARMS]: [
    EventTypes.DATA_EXPORT,
    EventTypes.ADD,
    EventTypes.EDIT,
    EventTypes.DISABLE,
    EventTypes.VIEW,
    EventTypes.SEARCH,
  ],
  [AuditLogModules.ANCILLARIES]: [
    EventTypes.DATA_EXPORT,
    EventTypes.ADD,
    EventTypes.EDIT,
    EventTypes.DISABLE,
    EventTypes.VIEW,
    EventTypes.SEARCH,
  ],
  [AuditLogModules.TRANSACTIONS]: [
    EventTypes.DATA_EXPORT,
    EventTypes.ADD,
    EventTypes.DISCARD,
    EventTypes.VIEW,
    EventTypes.SEARCH,
  ],
  [AuditLogModules.ADMINISTRATION]: [
    EventTypes.EDIT,
    EventTypes.DEACTIVATE,
    EventTypes.ACTIVATE,
    EventTypes.ADD,
    EventTypes.DELETE,
  ],
  [AuditLogModules.CUSTOM_FIELDS]: [
    EventTypes.EDIT,
    EventTypes.ADD,
    EventTypes.DISABLE,
    EventTypes.ENABLE,
    EventTypes.DELETE,
  ],
  [AuditLogModules.API_KEYS]: [
    EventTypes.ADD,
    EventTypes.EDIT,
    EventTypes.DELETE,
    EventTypes.ACTIVATE,
    EventTypes.DEACTIVATE,
  ],
  [AuditLogModules.WEAPON_LICENCE_CONFIGURATOR]: [
    EventTypes.VIEW,
    EventTypes.SEARCH,
    EventTypes.ADD,
    EventTypes.EDIT,
    EventTypes.DELETE,
    EventTypes.PUBLISH,
    EventTypes.UNPUBLISH,
    EventTypes.ACTIVATE,
    EventTypes.DEACTIVATE,
    EventTypes.CREATE,
  ],
  [AuditLogModules.WEAPON_LICENCES]: [
    EventTypes.VIEW,
    EventTypes.SEARCH,
    EventTypes.CREATE,
    EventTypes.VARY,
    EventTypes.REVOKE,
    EventTypes.SURRENDER,
    EventTypes.REACTIVATE,
    EventTypes.RENEW,
    EventTypes.SUSPEND,
    EventTypes.EXPIRE,
    EventTypes.ACTIVATE,
    EventTypes.EDIT,
    EventTypes.DATA_EXPORT,
    EventTypes.LINK,
    EventTypes.CANCEL,
  ],
  [AuditLogModules.GLOBAL_SEARCH]: [EventTypes.SEARCH],
  [AuditLogModules.CASE_MANAGEMENT]: [EventTypes.VIEW, EventTypes.SEARCH],
};

const filterEventType = (
  modules: string[],
  dictionary: IDictionary[] | null,
  hasWeaponLicence: boolean | null,
): IDictionary[] | null => {
  if (dictionary === null) {
    return null;
  }

  let filteredDictionary = dictionary;

  if (!hasWeaponLicence) {
    const weaponLicenceSpecificEventTypes = [
      EventTypes.CREATE,
      EventTypes.VARY,
      EventTypes.REVOKE,
      EventTypes.SURRENDER,
      EventTypes.REACTIVATE,
      EventTypes.RENEW,
      EventTypes.SUSPEND,
      EventTypes.EXPIRE,
      EventTypes.PUBLISH,
      EventTypes.UNPUBLISH,
      EventTypes.LINK,
      EventTypes.CANCEL,
    ].map((eventType) => eventType.toString());

    filteredDictionary = dictionary.filter(
      (dictionaryRecord) =>
        !weaponLicenceSpecificEventTypes.includes(dictionaryRecord.uniqueId.toString()),
    );
  }

  if (!modules.length) {
    return filteredDictionary;
  }

  const validModules: AuditLogModules[] = modules.map((module) =>
    getEnumByValue(AuditLogModules, module),
  );

  return filteredDictionary.filter((dictionaryRecord) =>
    validModules
      .flatMap((item) => filters[item])
      .map((moduleRecord) => moduleRecord.toString())
      .includes(dictionaryRecord.uniqueId.toString()),
  );
};

const compareUserNames = (user1: IUser, user2: IUser): number => {
  if (user1.firstName.toLowerCase() > user2.firstName.toLowerCase()) {
    return 1;
  }
  if (user1.firstName.toLowerCase() < user2.firstName.toLowerCase()) {
    return -1;
  }
  if (user1.lastName.toLowerCase() > user2.lastName.toLowerCase()) {
    return 1;
  }
  if (user1.lastName.toLowerCase() < user2.lastName.toLowerCase()) {
    return -1;
  }
  return 0;
};

const AuditLogSearchForm = ({
  onSubmit,
  isSearchButtonDisabled = false,
  initialSearchForm,
  perPage,
  currentPage,
}: IAuditLogSearchForm) => {
  const {
    control,
    setValue,
    handleSubmit,
    reset,
    getValues,
    watch,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm<IAuditLogSearchFormValues>({
    defaultValues: initialSearchForm,
  });

  const { t } = useTranslations();

  const { shortDateFormat, getProperties } = useGlobalProperty();
  const { selectedLanguage } = useLang();
  const { hasWeaponLicenceModule } = useWeaponLicenceModule();
  const hasWeaponLicence = hasWeaponLicenceModule();
  const { auditLogModule, auditLogEventType, auditLogEventName, loadDictionaries } =
    useAuditLog();
  const [userOptions, setUserOptions] = useState<IFormElementOptionProp[]>([]);
  const { users, getUsersData } = useUsers();
  const [values, setValues] = useState<IAuditLogSearchFormValues>(initialAuditLogSearchValues);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  useEffect(() => {
    getProperties();
    (async () => {
      await getUsersData(1, 9999, undefined);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (users?.length) {
      const usersOptions = users.sort(compareUserNames).map((user) => ({
        label: `${user.firstName} ${user.lastName} (${user.displayUniqueId})`,
        value: user.uniqueId,
      }));
      setUserOptions(usersOptions);
    }
  }, [users]);

  useEffect(() => {
    (async () => {
      await loadDictionaries([
        EDictionaryTypes.AuditLogModule,
        EDictionaryTypes.AuditLogEventType,
        EDictionaryTypes.AuditLogEventName,
      ]);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLanguage]);

  useEffect(() => {
    const subscription = watch((value) => {
      setValues(value as IAuditLogSearchFormValues);
    });
    return () => subscription.unsubscribe();
  }, [watch]);

  useEffect(() => {
    setValues(initialSearchForm);
    reset(initialSearchForm);
    handleSubmit(onSubmit)();
  }, [initialSearchForm]);

  useEffect(() => {
    if (!isInitialLoad) {
      handleSubmit(onSubmit)();
    } else {
      setIsInitialLoad(false);
    }
  }, [perPage, currentPage]);

  useEffect(() => {
    const subscription = watch((value) => {
      const message = t('general.validation.dateShouldBeLater.message');
      if (isDateAfter(getValues().eventDateFrom, value.eventDateTo as string)) {
        setError('eventDateTo', { type: 'custom', message });
      } else {
        clearErrors('eventDateTo');
      }
    });
    return () => subscription.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watch]);

  const handleResetField = (name?: string, refreshResults: boolean = true) => {
    if (name) {
      reset({ ...getValues(), [name]: '' });
    } else {
      reset(initialAuditLogSearchValues);
    }

    if (refreshResults) {
      onSubmit(getValues());
    }
  };

  const auditLogModuleData = React.useMemo(() => {
    if (!auditLogModule) return [];
    if (!hasWeaponLicence) {
      return auditLogModule.filter(
        (item) =>
          item.uniqueId !== AuditLogModules.WEAPON_LICENCE_CONFIGURATOR &&
          item.uniqueId !== AuditLogModules.WEAPON_LICENCES,
      );
    }
    return auditLogModule;
  }, [auditLogModule, hasWeaponLicence]);

  return (
    <SearchWrapper>
      <form onSubmit={handleSubmit(onSubmit)}>
        <StyledSearchForm
          container
          style={{ justifyContent: 'space-between' }}
          spacing={2}
          columns={10}
        >
          <Grid size={{ xs: 10, sm: 5, lg: 2 }}>
            <FormMultiSelect
              options={userOptions}
              name="accountsUniqueIds"
              setValue={setValue}
              canBeEmpty={true}
              label={t(EAuditLogSearchFormLabels.accountsUniqueIds)}
              control={control}
              errors={errors}
            />
          </Grid>
          <Grid size={{ xs: 10, sm: 5, lg: 2 }}>
            <FormMultiSelect
              options={mapDictionaryToOptionProp(auditLogModuleData)}
              name="modulesUniqueIds"
              setValue={setValue}
              onSelect={(arg) => {
                const modules: AuditLogModules[] = arg.map((item: string) =>
                  getEnumByValue(AuditLogModules, item),
                );
                const eventValue = getValues('eventTypesUniqueIds');
                const modulesFilters = modules.flatMap((item) => filters[item]);
                const isEventValidForNewModule = modulesFilters.map((moduleRecord) =>
                  eventValue.includes(moduleRecord.toString()),
                ).length;
                if (!isEventValidForNewModule) {
                  setValue('eventTypesUniqueIds', []);
                }
              }}
              canBeEmpty={true}
              label={t(EAuditLogSearchFormLabels.modulesUniqueIds)}
              control={control}
              errors={errors}
            />
          </Grid>
          <Grid size={{ xs: 10, sm: 12, lg: 2 }}>
            <FormMultiSelect
              options={mapDictionaryToOptionProp(
                filterEventType(
                  getValues('modulesUniqueIds'),
                  auditLogEventType,
                  hasWeaponLicenceModule(),
                ),
              )}
              name="eventTypesUniqueIds"
              setValue={setValue}
              canBeEmpty={true}
              label={t(EAuditLogSearchFormLabels.eventTypesUniqueIds)}
              control={control}
              errors={errors}
            />
          </Grid>
          <Grid size={{ xs: 10, sm: 12, lg: 2 }}>
            <FormMultiSelect
              options={mapDictionaryToOptionProp(
                filterEventName(
                  getValues('modulesUniqueIds'),
                  getValues('eventTypesUniqueIds'),
                  auditLogEventName,
                ),
              )}
              name="eventNamesUniqueIds"
              setValue={setValue}
              canBeEmpty={true}
              label={t(EAuditLogSearchFormLabels.eventNamesUniqueIds)}
              control={control}
              errors={errors}
            />
          </Grid>
          <Grid size={{ xs: 5, sm: 5, md: 5, lg: 2 }}>
            <FormDatePicker
              name="eventDateFrom"
              dateFormat={shortDateFormat}
              label={t(EAuditLogSearchFormLabels.eventDateFrom)}
              control={control}
              errors={errors}
            />
          </Grid>
          <Grid size={{ xs: 5, sm: 5, md: 5, lg: 2 }}>
            <FormDatePicker
              name="eventDateTo"
              dateFormat={shortDateFormat}
              label={t(EAuditLogSearchFormLabels.eventDateTo)}
              control={control}
              errors={errors}
              minDate={
                getValues().eventDateFrom ? (getValues().eventDateFrom as Date) : undefined
              }
            />
          </Grid>

          <Grid
            size={{ xs: 12 }}
            container
            columns={10}
            columnSpacing={2}
            sx={{ display: 'flex', justifyContent: 'flex-end' }}
          >
            <Grid size={{ xs: 10, sm: 10, md: 5, lg: 2 }}>
              <Button
                variant={EButtonVariants.contained}
                size={EButtonSizes.small}
                label={t('searchForAuditLog.search.button')}
                disabled={isSearchButtonDisabled}
                type="submit"
                fullWidth
                sx={{
                  mt: {
                    xs: 1,
                  },
                }}
              />
            </Grid>
          </Grid>
        </StyledSearchForm>
      </form>
      <SelectedFilters
        name="auditLog"
        values={values}
        labels={EAuditLogSearchFormLabels}
        handleDelete={handleResetField}
        dictionaries={{
          accountsUniqueIds: prepareFilters(userOptions, 'value', 'label'),
          modulesUniqueIds: prepareFilters(auditLogModule, 'uniqueId', 'name'),
          eventTypesUniqueIds: prepareFilters(auditLogEventType, 'uniqueId', 'name'),
          eventNamesUniqueIds: prepareFilters(auditLogEventName, 'uniqueId', 'name'),
        }}
        noPreset
        showSaveFilters={false}
        saveRouterStateAfterClearAll={false}
      />
    </SearchWrapper>
  );
};

export default AuditLogSearchForm;
